import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { StructuredOutput } from "../../../api/threads/liveMessages";
import { newClientKey, talkWithAgent, type HumanInputRequest } from "../../../api/threads/threadsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useSubmission } from "../../../shared/state/useSubmission";
import AppButton from "../../../ui/buttons/AppButton";
import Field from "../../../ui/forms/Field";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";

interface TalkWithAgentProps {
  readonly threadId: string;
  /** The waiting review step's request. */
  readonly reviewEntry: number;
  /** What the review shows, by its output kind, so the prompt names it; `null` when unknown. */
  readonly reviewed: StructuredOutput["output_kind"] | null;
  /** The result as the review form holds it now, edits included; `null` when unchanged. */
  readonly result: HumanInputRequest["result"];
  readonly disabled: boolean;
  /** Why this viewer can't talk with the agent (module C's decision); the button says it. */
  readonly unavailable?: string | undefined;
  /** The agent has the request, and the edits went with it. */
  readonly onSent: () => void;
}

/**
 * Talk with the agent of a waiting review (F105): the person's edits and request go to the
 * agent that made the reviewed result, in its own session; the review then asks again with
 * its new result. The server decides whether the review offers it and which session runs it.
 * The prompt says so before sending (TR-B1b-04): sending renews the review, also for a question.
 */
export default function TalkWithAgent({ threadId, reviewEntry, reviewed, result, disabled, unavailable, onSent }: TalkWithAgentProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState("");
  const [clientRequestId] = useState(newClientKey);
  const { state, submit } = useSubmission();
  const settled = state.status === "submitting" || state.status === "succeeded";

  if (!open) {
    return (
      <AppButton variant="secondary" size="sm" disabled={disabled} unavailable={unavailable} onClick={() => { setOpen(true); }}>
        {t("threads.talk.open")}
      </AppButton>
    );
  }
  const send = () => {
    submit(async () => {
      await talkWithAgent(threadId, { reviewEntry, request: request.trim(), result, clientRequestId });
      onSent();
    });
  };
  return (
    <div className="space-y-2">
      <Field label={t("threads.talk.request")} hint={t(`threads.talk.renews.${renewedItem(reviewed)}`)}>
        {(control) => (
          <textarea {...control} className={FORM_CONTROL_CLASS} rows={3} value={request} disabled={settled} onChange={(event) => { setRequest(event.target.value); }} />
        )}
      </Field>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <div className="flex flex-wrap items-center gap-2">
        <AppButton variant="primary" size="sm" disabled={disabled || settled || request.trim() === ""} unavailable={unavailable} onClick={send}>
          {t("threads.talk.send")}
        </AppButton>
        <AppButton variant="ghost" size="sm" disabled={settled} onClick={() => { setOpen(false); }}>
          {t("threads.talk.cancel")}
        </AppButton>
      </div>
    </div>
  );
}

/** The reviewed item's word in the prompt's hint; a result of any other kind is "the result". */
function renewedItem(reviewed: StructuredOutput["output_kind"] | null): "annotations" | "instance_data" | "result" {
  return reviewed === "annotations" || reviewed === "instance_data" ? reviewed : "result";
}
